import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LITE_SIGNUP_URL } from '../../data/links';
import HeroBackdrop from '../../components/HeroBackdrop';

/**
 * Geofleet Lite hero: headline on a deep navy band, then real Lite screens taken from the
 * playground (Singapore sample workspace): the dashboard in a browser frame, flanked by the
 * driver app and the customer's tracking page on phones.
 */

type Fact = { icon: string; label: string };

function useArray<T>(key: string): T[] {
  const { t, i18n } = useTranslation();
  return useMemo(() => {
    const raw = t(key, { returnObjects: true, lng: i18n.language });
    return Array.isArray(raw) ? (raw as T[]) : [];
  }, [t, key, i18n.language]);
}

function Phone({ src, alt, className = '' }: { src: string; alt: string; className?: string }) {
  return (
    <div className={`rounded-[2.2rem] bg-gray-950 p-2 shadow-2xl shadow-blue-950/40 ring-1 ring-white/10 ${className}`}>
      <div className="overflow-hidden rounded-[1.75rem] bg-white">
        <img src={src} alt={alt} width={390} height={844} loading="eager" decoding="async" className="block h-auto w-full" />
      </div>
    </div>
  );
}

export default function LiteHero() {
  const { t } = useTranslation();
  const facts = useArray<Fact>('litePage.heroFacts');

  return (
    <section className="relative overflow-hidden bg-[#071421]" aria-labelledby="lite-hero-title">
      <HeroBackdrop />

      <div className="relative mx-auto max-w-7xl px-4 pt-28 sm:px-6 lg:pt-32">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-blue-400/30 bg-blue-500/10 px-3.5 py-1.5 text-xs font-semibold text-blue-200">
            <span className="rounded-full bg-blue-500 px-1.5 py-px text-[10px] font-bold uppercase tracking-wider text-white">{t('nav.new')}</span>
            {t('litePage.heroEyebrow')}
          </span>
          <h1 id="lite-hero-title" className="mt-6 text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-[3.5rem] lg:leading-[1.08]">
            {t('litePage.heroTitle')}{' '}
            <span className="block text-[#76a9ff]">
              {t('litePage.heroTitleHighlight')}
            </span>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-slate-300 lg:text-lg">{t('litePage.heroLead')}</p>

          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <a
              href={LITE_SIGNUP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/30 transition-all hover:-translate-y-0.5 hover:bg-blue-500"
            >
              {t('litePage.ctaPrimary')}
              <i className="ri-arrow-right-line rtl:rotate-180" aria-hidden />
            </a>
            <a
              href="#lite-day"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10"
            >
              {t('litePage.ctaSecondary')}
            </a>
          </div>

          {facts.length > 0 && (
            <ul className="m-0 mt-7 flex list-none flex-wrap justify-center gap-x-6 gap-y-2 p-0 text-sm text-slate-300">
              {facts.map((f) => (
                <li key={f.label} className="flex items-center gap-1.5">
                  <i className={`${f.icon} text-emerald-400`} aria-hidden />
                  {f.label}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Product */}
        <div className="relative mx-auto mt-12 max-w-6xl pb-16 lg:mt-14 lg:pb-24">
          {/* White lower half: the screenshot sits across the navy/white edge. */}
          <div className="absolute -inset-x-[100vw] bottom-0 top-[45%] hidden bg-white md:block" aria-hidden />
          <figure className="relative m-0 overflow-hidden rounded-xl border border-white/10 bg-white shadow-[0_40px_120px_-30px_rgba(2,6,23,0.7)] ring-1 ring-black/5 lg:rounded-2xl">
            <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-4 py-2.5">
              <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
              <span className="mx-auto hidden w-full max-w-xs items-center justify-center gap-1.5 rounded-md bg-white px-3 py-1 text-[11px] text-gray-500 ring-1 ring-gray-200 sm:flex">
                <i className="ri-lock-line" aria-hidden />
                Geofleet Lite
              </span>
            </div>
            <img
              src="/images/lite/dashboard.webp"
              alt={t('litePage.heroVisualAlt')}
              width={1920}
              height={1200}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="block h-auto w-full"
            />
          </figure>

          <Phone
            src="/images/lite/phone-driver.webp"
            alt={t('litePage.heroDriverAlt')}
            className="absolute -bottom-2 start-[-1.5rem] hidden w-[190px] -rotate-3 md:block lg:start-[-3.5rem] lg:w-[230px]"
          />
          <Phone
            src="/images/lite/phone-track.webp"
            alt={t('litePage.heroTrackAlt')}
            className="absolute -bottom-6 end-[-1.5rem] hidden w-[190px] rotate-3 md:block lg:end-[-3.5rem] lg:w-[230px]"
          />

          {/* Phones sit under the screenshot on small screens. */}
          <div className="relative mt-6 grid grid-cols-2 gap-4 md:hidden">
            <Phone src="/images/lite/phone-driver.webp" alt={t('litePage.heroDriverAlt')} />
            <Phone src="/images/lite/phone-track.webp" alt={t('litePage.heroTrackAlt')} />
          </div>
        </div>
      </div>
    </section>
  );
}
